package eu.monniot.feed.shared.api

import io.ktor.client.*
import io.ktor.client.call.*
import io.ktor.client.plugins.*
import io.ktor.client.request.*
import io.ktor.http.*
import io.ktor.http.content.*

/**
 * Outcome of an on-demand "fetch now" upstream pull (§5.2). Lets callers
 * distinguish a successful pull from a rate-limit (429) — the latter is the
 * signal to silently fall back to a plain DB re-read (§5.3) rather than surface
 * an error.
 */
sealed class RefreshResult {
    /** The server triggered an upstream fetch of [feedsFetched] feeds. */
    data class Success(val feedsFetched: Int) : RefreshResult()

    /**
     * The server rejected the request with 429 (the global refresh window is
     * still open). [retryAfterSeconds] is parsed from the `Retry-After` header
     * when present, else null. Callers should NOT show an error — re-read the
     * list silently and update the "Synced … ago" line.
     */
    data class RateLimited(val retryAfterSeconds: Long?) : RefreshResult()
}

class FeedApi(private val client: HttpClient) {

    suspend fun checkHealth(): HealthResponse =
        client.get("v1/health").body()

    suspend fun getVersion(): VersionResponse =
        client.get("v1/version").body()

    suspend fun getFeeds(): ApiResponse<List<Feed>> =
        client.get("v1/feeds").body()

    suspend fun addFeed(request: FeedAddRequest): ApiResponse<FeedAddResponse> =
        client.post("v1/feeds") {
            contentType(ContentType.Application.Json)
            setBody(request)
        }.body()

    suspend fun updateFeed(feedId: Int, request: FeedUpdateRequest): ApiResponse<UpdateResponse> =
        client.put("v1/feeds/$feedId") {
            contentType(ContentType.Application.Json)
            setBody(request)
        }.body()

    suspend fun deleteFeed(feedId: Int) {
        client.delete("v1/feeds/$feedId")
    }

    suspend fun markArticleRead(
        articleId: Int,
        request: ArticleReadUpdateRequest
    ): ApiResponse<UpdatedCountResponse> =
        client.put("v1/articles/$articleId/read") {
            contentType(ContentType.Application.Json)
            setBody(request)
        }.body()

    // --- Batch read operations (#9) ---

    /**
     * Mark a specific set of articles read/unread in one request. Hits
     * `POST /v1/articles/read`. The optimistic, offline-capable callers
     * ([eu.monniot.feed.shared.SharedFeedRepository], [eu.monniot.feed.shared.sync.SyncEngine])
     * write the local mirror themselves before/around this call, so no
     * follow-up [sync] is needed here. Callers must send at most
     * [MAX_ARTICLE_IDS_PER_BATCH] ids per call — split larger selections into
     * chunks.
     */
    suspend fun markArticlesRead(request: MarkReadRequest): ApiResponse<UpdatedCountResponse> =
        client.post("v1/articles/read") {
            contentType(ContentType.Application.Json)
            setBody(request)
        }.body()

    // Note: the client implements mark-all-read / mark-feed-read as a batched
    // fan-out over the locally-mirrored unread ids through [markArticlesRead],
    // so those flows stay optimistic and offline-capable. The server's former
    // whole-view bulk endpoints (`POST /v1/articles/read-all` and
    // `POST /v1/feeds/{id}/read`) were removed as client-orphaned in #122.

    companion object {
        /**
         * Max ids per [markArticlesRead] call. The server binds one SQL host
         * parameter per id (`db.rs::mark_articles_read`), and SQLite caps host
         * parameters at `SQLITE_MAX_VARIABLE_NUMBER` (999 on older builds,
         * 32,766 on modern ones). Callers with a larger selection must split
         * it into chunks of at most this size.
         */
        const val MAX_ARTICLE_IDS_PER_BATCH = 500
    }

    suspend fun getStats(): ApiResponse<Stats> =
        client.get("v1/stats").body()

    suspend fun getCategories(): ApiResponse<List<Category>> =
        client.get("v1/categories").body()

    suspend fun createCategory(request: CategoryCreateRequest): ApiResponse<CategoryCreateResponse> =
        client.post("v1/categories") {
            contentType(ContentType.Application.Json)
            setBody(request)
        }.body()

    suspend fun setFeedCategory(feedId: Int, request: FeedCategoryUpdateRequest): ApiResponse<UpdateResponse> =
        client.put("v1/feeds/$feedId/category") {
            contentType(ContentType.Application.Json)
            setBody(request)
        }.body()

    /** Rename a category. Hits `PUT /v1/categories/{id}` — 204 No Content on success. */
    suspend fun updateCategory(categoryId: Int, request: CategoryUpdateRequest) {
        client.put("v1/categories/$categoryId") {
            contentType(ContentType.Application.Json)
            setBody(request)
        }
    }

    /**
     * Delete a category. Hits `DELETE /v1/categories/{id}` — 204 No Content on success.
     *
     * The server does NOT take a reassign target: any feeds still assigned to
     * this category fall back to uncategorized via `ON DELETE SET NULL`.
     * Callers wanting "delete with reassign" must move the category's feeds to
     * the target category first (via [setFeedCategory]) before calling this —
     * see [eu.monniot.feed.shared.SharedFeedRepository.deleteCategory].
     */
    suspend fun deleteCategory(categoryId: Int) {
        client.delete("v1/categories/$categoryId")
    }

    /**
     * Reorder categories. Hits `POST /v1/categories/reorder` — 204 No Content
     * on success. Web-only feature (drag-to-reorder, ticket #123).
     */
    suspend fun reorderCategories(request: ReorderCategoriesRequest) {
        client.post("v1/categories/reorder") {
            contentType(ContentType.Application.Json)
            setBody(request)
        }
    }

    /**
     * Reorder feeds within a category. Hits `POST /v1/feeds/reorder` — 204 No
     * Content on success. Web-only feature (drag-to-reorder, ticket #133).
     */
    suspend fun reorderFeeds(request: ReorderFeedsRequest) {
        client.post("v1/feeds/reorder") {
            contentType(ContentType.Application.Json)
            setBody(request)
        }
    }

    /**
     * Import feeds from OPML XML content.
     * Sends the raw XML text as `text/xml` to `POST /v1/feeds/import/opml`.
     */
    suspend fun importOpml(opmlText: String): ApiResponse<OpmlImportResult> =
        client.post("v1/feeds/import/opml") {
            setBody(TextContent(opmlText, ContentType.Text.Xml))
        }.body()

    /**
     * Returns the most recent parse error for a feed, or null if the server
     * responds with 404 (no error on record).
     *
     * With `expectSuccess = true` in both HttpClientFactory implementations, Ktor
     * throws [ClientRequestException] before the response body can be inspected.
     * We catch it here: a 404 maps to null (no parse error on record), any other
     * status is re-thrown so callers still see unexpected failures.
     */
    suspend fun getParseError(feedId: Int): ApiResponse<FeedParseError>? {
        return try {
            client.get("v1/feeds/$feedId/parse-error").body()
        } catch (e: ClientRequestException) {
            if (e.response.status == HttpStatusCode.NotFound) null
            else throw e
        }
    }

    /**
     * Send a client error/diagnostic report to `POST /v1/client-events`. The
     * endpoint is unauthenticated and returns an empty 200 body, so we don't
     * deserialize a response. Callers should guard against failures (the beacon
     * itself can fail) — see [ClientEventReporter].
     */
    suspend fun reportClientEvent(request: ClientEventRequest) {
        client.post("v1/client-events") {
            contentType(ContentType.Application.Json)
            setBody(request)
        }
    }

    // --- Sync ---

    /**
     * Fetch article changes since the given cursor. Hits `GET /v1/sync`.
     *
     * The response is a top-level JSON object — NOT wrapped in [ApiResponse] —
     * and is either a [SyncResponse.Delta] (articles + tombstones + cursor) or
     * [SyncResponse.FullResync] (client must clear and re-backfill from 0).
     *
     * @param since Sync cursor — the `cursor` value from the last delta, or 0 for
     *              a fresh backfill.
     * @param limit Optional page size; the server defaults to 500 and clamps at 2000.
     */
    suspend fun sync(since: Long, limit: Int? = null): SyncResponse =
        client.get("v1/sync") {
            parameter("since", since)
            limit?.let { parameter("limit", it) }
        }.body()

    // --- Settings ---

    suspend fun getRetention(): RetentionResponse =
        client.get("v1/settings/retention").body()

    suspend fun setRetention(request: RetentionRequest): RetentionResponse =
        client.put("v1/settings/retention") {
            contentType(ContentType.Application.Json)
            setBody(request)
        }.body()

    // --- On-demand "fetch now" upstream pull (§5.2/§5.3) ---

    /**
     * Trigger an immediate upstream fetch of ALL feeds (the primary "fetch now"
     * gesture). Returns [RefreshResult.Success] with the count of feeds pulled,
     * or [RefreshResult.RateLimited] when the server's global refresh window is
     * exhausted (429) — the caller then silently falls back to a plain re-read.
     *
     * A 429 is mapped to a typed result rather than thrown so the rate-limit
     * path (the common, expected case) doesn't go through exception handling.
     * Any other non-2xx status still throws (Ktor `expectSuccess = true`).
     */
    suspend fun refreshAllFeeds(): RefreshResult =
        runRefresh("v1/feeds/refresh")

    /**
     * Trigger an immediate upstream fetch of a single feed (the secondary,
     * per-feed gesture, surfaced in the subscription overflow menu). Same 429 →
     * [RefreshResult.RateLimited] mapping as [refreshAllFeeds].
     */
    suspend fun refreshFeed(feedId: Int): RefreshResult =
        runRefresh("v1/feeds/$feedId/refresh")

    private suspend fun runRefresh(path: String): RefreshResult {
        return try {
            val body: RefreshResponse = client.post(path).body()
            RefreshResult.Success(body.feeds_fetched)
        } catch (e: ClientRequestException) {
            if (e.response.status == HttpStatusCode.TooManyRequests) {
                val retryAfter = e.response.headers["Retry-After"]?.toLongOrNull()
                RefreshResult.RateLimited(retryAfter)
            } else {
                throw e
            }
        }
    }

}
